import React, { Fragment, memo, useCallback, useState } from 'react'
import { Typography } from '@mui/material'
import { Formik, Form } from 'formik'
import { Button } from 'src/components/shared/Button'
import { CommonModal } from 'src/components/shared/CommonModal'
import { WebhookResourceType } from 'src/constants/webhook/enums'
import type { SendTestWebhookFormValues } from './SendTestWebhookModal.interface'
import { sendTestWebhookValidator } from './SendTestWebhookModal.validator'
import { SendTestWebhookFields } from './SendTestWebhookFields'
import { SendTestWebhookSubmitButton } from './SendTestWebhookSubmitButton'

const FORM_ID = 'send-test-webhook-form'

const INITIAL_VALUES: SendTestWebhookFormValues = {
  eventType: '',
  resourceType: WebhookResourceType.Contractor,
  resourceId: '',
}

interface Props {
  isOpen: boolean
  onClose(): void
  onSend(values: SendTestWebhookFormValues): void
}

/**
 * "Send test event" is a one-off action, so it opens from the Deliveries tab's CTA as a form
 * modal (like "New subscription" on Subscriptions) rather than owning a tab of its own.
 * Submitting only opens the confirm on top — nothing is dispatched until the admin confirms,
 * because the request is a live outbound call. Confirming sends and closes both.
 *
 * `Formik` wraps the `CommonModal` so the footer's submit button, which MUI portals out of the
 * `<Form>` element, still reads the form's gate through context.
 */
const SendTestWebhookModalComponent = ({ isOpen, onClose, onSend }: Props) => {
  const [pendingValues, setPendingValues] = useState<SendTestWebhookFormValues | null>(null)

  const handleSubmit = useCallback((values: SendTestWebhookFormValues) => {
    setPendingValues(values)
  }, [])

  const handleCancelConfirm = useCallback(() => {
    setPendingValues(null)
  }, [])

  const handleClose = useCallback(() => {
    setPendingValues(null)
    onClose()
  }, [onClose])

  const handleConfirm = useCallback(() => {
    if (!pendingValues) {
      return
    }

    onSend(pendingValues)
    setPendingValues(null)
    onClose()
  }, [pendingValues, onSend, onClose])

  if (!isOpen) {
    // Unmounting resets Formik, so the next open starts from a clean form.
    return null
  }

  return (
    <Formik<SendTestWebhookFormValues>
      initialValues={INITIAL_VALUES}
      validationSchema={sendTestWebhookValidator}
      onSubmit={handleSubmit}
    >
      <Fragment>
        <CommonModal
          isOpen={isOpen}
          title="Send test event"
          onClose={handleClose}
          buttons={
            <Fragment>
              <Button size="small" color="grey" onClick={handleClose}>
                Cancel
              </Button>
              <SendTestWebhookSubmitButton formId={FORM_ID} />
            </Fragment>
          }
        >
          <Form id={FORM_ID} data-testid="send-test-webhook-form">
            <SendTestWebhookFields />
          </Form>
        </CommonModal>
        <CommonModal
          isOpen={Boolean(pendingValues)}
          title="Send a live test event?"
          onClose={handleCancelConfirm}
          buttons={
            <Fragment>
              <Button size="small" color="grey" onClick={handleCancelConfirm}>
                Cancel
              </Button>
              <Button
                size="small"
                color="dark"
                onClick={handleConfirm}
                data-testid="send-test-webhook-confirm-button"
              >
                Send test event
              </Button>
            </Fragment>
          }
        >
          <Typography variant="body2">
            This posts a real HTTP request to the customer&apos;s callback URL right now. The
            payload is test data, but the delivery is real and will appear in the Deliveries tab.
          </Typography>
        </CommonModal>
      </Fragment>
    </Formik>
  )
}

export const SendTestWebhookModal = memo(SendTestWebhookModalComponent)
